import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Trophy, Home, Users, Briefcase, CheckCircle2, ChevronRight, ChevronLeft, X, Target, ShieldCheck, Vote } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTenant } from '../../contexts/TenantContext';

interface Template {
  id: string;
  name: string;
  desc: string;
  icon: React.ReactNode;
  suggestedRoles: string[];
  suggestedFeatures: string[];
  tips: string[];
}

const TEMPLATES: Template[] = [
  {
    id: 'sports',
    name: 'Youth Sports / Little League',
    desc: 'Focus on roster management, safety requirements, and coach appointments.',
    icon: <Trophy size={18} className="text-blue-500" />,
    suggestedRoles: ['President', 'Commissioner', 'Coach', 'Team Parent', 'Player'],
    suggestedFeatures: ['Requirement Tracking (Birth Certs)', 'Schedules', 'Treasurer Ledger'],
    tips: [
      'Note: In Little Leagues, coaches are typically appointed by the board, not elected by members.',
      'Consider making player profiles private to guardians and coaches only.'
    ]
  },
  {
    id: 'hoa',
    name: 'HOA / Community Board',
    desc: 'Manage property blocks, dues collection, and democratic board elections.',
    icon: <Home size={18} className="text-emerald-500" />,
    suggestedRoles: ['Board President', 'Property Manager', 'Committee Chair', 'Resident'],
    suggestedFeatures: ['Violation Tracking', 'Public Ledger', 'Digital Voting', 'Vault'],
    tips: [
      'Enable "Digital Voting" for annual board elections.',
      'Use the Ledger for transparent budget reporting to all residents.'
    ]
  },
  {
    id: 'union',
    name: 'Labor Union / Local',
    desc: 'Coordinate chapters, grievance tracking, and membership standing.',
    icon: <ShieldCheck size={18} className="text-red-500" />,
    suggestedRoles: ['Local President', 'Shop Steward', 'Treasurer', 'Member'],
    suggestedFeatures: ['Grievance Vault', 'Dues Split (Vertical)', 'Elections'],
    tips: [
      'Use "Vertical Split" to automatically route a % of dues to the national council.',
      'Keep the Grievance Vault restricted to Stewards and above.'
    ]
  },
  {
    id: 'guardian',
    name: 'Guardian-Led (Child / Pet)',
    desc: 'Perfect for schools or pet clubs where sponsors manage dependent accounts.',
    icon: <Users size={18} className="text-amber-500" />,
    suggestedRoles: ['Sponsor', 'Dependent', 'Admin', 'Staff'],
    suggestedFeatures: ['Guardian Linking', 'Proxy Ledger', 'Shared Inbox'],
    tips: [
      'Enable "Guardian Linking" so parents can manage their children\'s dues and schedules.',
      'Sponsors have specific authority within this Org, but no visibility if the dependent joins unrelated Orgs.',
      'Proxy Ledger ensures that bills sent to a Dependent appear in the Guardian\'s dashboard.'
    ]
  },
  {
    id: 'generic',
    name: 'Generic / Custom',
    desc: 'A clean slate for any group structure.',
    icon: <Target size={18} className="text-purple-500" />,
    suggestedRoles: ['President', 'Vice President', 'Secretary', 'Treasurer', 'Member'],
    suggestedFeatures: ['Pulse', 'Directory', 'Ledger'],
    tips: [
      'You can rename any role later in the Settings > Terminology page.'
    ]
  }
];

export const NodeCreationWizard = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { brand } = useTenant();
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [orgName, setOrgName] = useState('');

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-4xl bg-white rounded-none shadow-2xl overflow-hidden flex flex-col md:flex-row h-[600px] dark:bg-slate-900"
      >
        {/* Sidebar */}
        <div className="w-full md:w-64 bg-navy-900 p-8 text-white flex flex-col justify-between">
          <div>
            <h2 className="text-2xl font-display mb-8">{brand.name} Wizard</h2>
            <div className="space-y-6">
              {[
                { s: 1, n: 'Identify', desc: 'Who are you?' },
                { s: 2, n: 'Select Template', desc: 'Find your structure' },
                { s: 3, n: 'Review & Build', desc: 'Customization' }
              ].map((item) => (
                <div key={item.s} className="flex gap-4 items-start">
                  <div className={`w-6 h-6 rounded-none flex items-center justify-center text-[10px] font-bold border-2 border-black dark:border-white transition-colors ${step >= item.s ? 'bg-white text-navy-900 border-white dark:bg-slate-900' : 'text-navy-400 border-navy-700'}`}>
                    {step > item.s ? <CheckCircle2 size={12} /> : item.s}
                  </div>
                  <div>
                    <p className={`text-xs font-bold uppercase tracking-widest ${step >= item.s ? 'text-white' : 'text-navy-400'}`}>{item.n}</p>
                    <p className="text-[10px] text-navy-400 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] text-navy-500 uppercase font-mono tracking-tighter">
            System Guide v1.0 • Org Hierarchy
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 flex flex-col bg-white overflow-y-auto dark:bg-slate-950">
          <div className="flex justify-end mb-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div 
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Let's name your group</h3>
                    <p className="text-sm text-gray-500">This will be the primary name of your organization or chapter.</p>
                  </div>
                  <Input 
                    label="Organization Name" 
                    placeholder="e.g. Springfield Little League" 
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                  />
                  <div className="p-4 bg-blue-50 border-2 border-black dark:border-white border-blue-100 rounded-none flex gap-3 items-start">
                    <Target className="text-blue-600 mt-0.5" size={20} />
                    <div>
                      <p className="text-xs font-bold text-blue-900 uppercase">Tip</p>
                      <p className="text-xs text-blue-800 mt-1 leading-relaxed">
                        If this is a branch of an existing organization, you can nest it later. Start with your primary internal name.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div 
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">What kind of organization is this?</h3>
                    <p className="text-sm text-gray-500">Choosing a template pre-configures your terminology, roles, and allowed features.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplate(t)}
                        className={`text-left p-4 rounded-none border-2 transition-all ${selectedTemplate?.id === t.id ? 'bg-white border-brand-primary shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] ring-4 ring-brand-primary/10 dark:bg-slate-900' : 'bg-white/50 border-transparent hover:border-black dark:border-white dark:bg-slate-900'}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-none ${selectedTemplate?.id === t.id ? 'bg-navy-900 text-white' : 'bg-white dark:bg-slate-800'}`}>
                            {t.icon}
                          </div>
                          <span className="font-bold text-gray-900 text-sm dark:text-white">{t.name}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 3 && selectedTemplate && (
                <motion.div 
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Configuring {orgName}</h3>
                      <p className="text-sm text-gray-500">Based on your {selectedTemplate.name} template choice.</p>
                    </div>
                    <Badge variant="blue">{selectedTemplate.name}</Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div className="space-y-4">
                      <div className="p-4 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                          <Users size={12} /> Suggested Roles
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.suggestedRoles.map(r => (
                            <Badge key={r} variant="gray" className="bg-white text-gray-700 border-none dark:bg-slate-800">{r}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="p-4 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                          <CheckCircle2 size={12} /> Enabled Features
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.suggestedFeatures.map(f => (
                            <Badge key={f} variant="amber" className="bg-amber-50 text-amber-900 border-none">{f}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-5 bg-navy-900 text-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4 relative overflow-hidden">
                        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-white/5 rounded-none blur-2xl dark:bg-slate-900" />
                        <h4 className="text-xs font-bold uppercase tracking-widest text-brand-primary-light flex items-center gap-2">
                          <ShieldCheck size={14} /> Expert Tips
                        </h4>
                        <ul className="space-y-3">
                          {selectedTemplate.tips.map((tip, idx) => (
                            <li key={idx} className="text-xs text-blue-100 leading-relaxed flex gap-2">
                              <span className="text-brand-primary-light font-bold">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="p-4 bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-900">
                        <p className="text-[10px] text-gray-400 leading-relaxed text-center">
                          You can fully customize these permissions and roles later in the specialized Configuration module.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-8 flex justify-between gap-4 pt-4 border-t border-black dark:border-white">
            <Button 
              variant="secondary" 
              onClick={step === 1 ? onClose : prevStep}
              className="px-6"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            <Button 
              variant="primary" 
              onClick={step === 3 ? onClose : nextStep}
              disabled={step === 1 && !orgName}
              className="px-8 bg-navy-900 hover:bg-navy-800"
            >
              {step === 3 ? 'Deploy Organization' : 'Next Step'}
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
